/**
 * The Veeva user agent's chat (ADR-0038): saved conversations
 * (`app.chat`, surface `user_admin`), a composer, and — under any answer
 * that proposed a change — its proposal card with Confirm / Cancel.
 *
 * The agent can look users up and *propose* creations and updates; it cannot
 * change Veeva. Every model call is recorded server-side.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  ActionIcon,
  Alert,
  Button,
  Group,
  Loader,
  Modal,
  NavLink,
  Paper,
  ScrollArea,
  Stack,
  Text,
  Textarea,
  TextInput,
  ThemeIcon,
} from "@mantine/core";
import { IconPlus, IconRobot, IconSend, IconTrash, IconUser } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { AnswerBody } from "@/features/solutions/explore/AnswerBody";
import {
  ChatModelPicker,
  chatModelLabel,
  choiceToRequest,
  PLATFORM_CHOICE_ID,
  defaultChoiceWithPlatform,
  useChatModels,
  type ChatModelChoice,
} from "@/features/solutions/shared";
import {
  deleteChat,
  listChats,
  listMessages,
  type ChatOut,
  type MessageOut,
} from "@/shared/api/ai";
import { createAgentChat, sendAgentMessage, type ChangeOut } from "@/shared/api/userAdmin";
import { toastApiError } from "@/shared/ui/toast";
import { ProposalCard } from "./ProposalCard";

export const BASE_PATH = "/solutions/validation/second-line-support";

const EXAMPLES = [
  "Show me all inactive users",
  "Find the user with email jane.doe@example.com",
  "Create a new user for John Smith, john.smith@example.com, as a document user",
  "Change the title of user 12345 to QA Lead",
];

function NewChatModal({
  opened,
  onClose,
  onCreated,
}: {
  opened: boolean;
  onClose: () => void;
  onCreated: (chat: ChatOut) => void;
}) {
  const getToken = useAccessToken();
  const { options, platform, error } = useChatModels(opened);
  const [name, setName] = useState("");
  const [choice, setChoice] = useState<ChatModelChoice>({ connectionId: null, deployment: null });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Default to the pinned platform model: the agent makes several
    // tool-calling turns per message and needs a dependable deployment.
    if (options)
      setChoice((c) =>
        defaultChoiceWithPlatform(
          options,
          platform,
          c.connectionId || !platform
            ? c
            : { connectionId: PLATFORM_CHOICE_ID, deployment: platform.deployment },
        ),
      );
  }, [options, platform]);

  const create = async () => {
    setBusy(true);
    try {
      const wire = choiceToRequest(choice);
      const chat = await createAgentChat(await getToken(), {
        name: name.trim(),
        connection_id: wire.connection_id,
        deployment: wire.deployment,
      });
      setName("");
      onCreated(chat);
    } catch (err) {
      toastApiError("Could not create the chat", err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title="New 2nd line support chat">
      <Stack gap="sm">
        <TextInput
          label="Name"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          required
          data-autofocus
        />
        <ChatModelPicker
          options={options}
          platform={platform}
          error={error}
          value={choice}
          onChange={setChoice}
          footnote="The model needs tool calling. The platform model supports it."
        />
        <Button
          onClick={() => void create()}
          loading={busy}
          disabled={!name.trim() || !choice.connectionId}
        >
          Create chat
        </Button>
      </Stack>
    </Modal>
  );
}

function Turn({ message, proposals }: { message: MessageOut; proposals: Map<string, ChangeOut> }) {
  const isUser = message.role === "user";
  return (
    <Group align="flex-start" gap="sm" wrap="nowrap" data-testid={`turn-${message.role}`}>
      <ThemeIcon variant="light" color={isUser ? "gray" : "blue"} radius="xl">
        {isUser ? <IconUser size={16} /> : <IconRobot size={16} />}
      </ThemeIcon>
      <Stack gap={6} style={{ minWidth: 0, flex: 1 }}>
        {isUser ? (
          <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
            {message.content}
          </Text>
        ) : (
          <AnswerBody content={message.content} citations={[]} />
        )}
        {(message.proposal_ids ?? []).map((id) => (
          <ProposalCard key={id} changeId={id} initial={proposals.get(id)} />
        ))}
        {!isUser && (message.model_reported ?? message.model) && (
          <Text size="xs" c="dimmed">
            {message.model_reported ?? message.model}
          </Text>
        )}
      </Stack>
    </Group>
  );
}

export function AgentChat({
  chatId,
  connectionId,
}: {
  chatId: string | null;
  connectionId: string | null;
}) {
  const getToken = useAccessToken();
  const navigate = useNavigate();
  const [chats, setChats] = useState<ChatOut[] | null>(null);
  const [messages, setMessages] = useState<MessageOut[]>([]);
  const [proposals, setProposals] = useState<Map<string, ChangeOut>>(new Map());
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const bottom = useRef<HTMLDivElement>(null);

  const loadChats = useCallback(async () => {
    try {
      setChats((await listChats(await getToken(), "user_admin")).items);
    } catch (err) {
      toastApiError("Could not load your chats", err);
      setChats([]);
    }
  }, [getToken]);

  useEffect(() => {
    void loadChats();
  }, [loadChats]);

  useEffect(() => {
    if (!chatId) {
      setMessages([]);
      return;
    }
    let live = true;
    void (async () => {
      try {
        const out = await listMessages(await getToken(), chatId);
        if (live) setMessages(out.items);
      } catch (err) {
        toastApiError("Could not load the conversation", err);
      }
    })();
    return () => {
      live = false;
    };
  }, [chatId, getToken]);

  useEffect(() => {
    bottom.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length, sending]);

  const chat = chats?.find((c) => c.chat_id === chatId) ?? null;

  const send = async (text: string) => {
    if (!chatId || !connectionId || !text.trim()) return;
    setSending(true);
    const optimistic: MessageOut = {
      message_id: `pending-${Date.now()}`,
      seq: messages.length,
      role: "user",
      content: text,
      citations: [],
      grounded: null,
      refused: false,
      proposal_ids: [],
      created_at_utc: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimistic]);
    setDraft("");
    try {
      const turn = await sendAgentMessage(await getToken(), chatId, {
        content: text,
        veeva_connection_id: connectionId,
      });
      setProposals((prev) => {
        const next = new Map(prev);
        for (const p of turn.proposals) next.set(p.change_id, p);
        return next;
      });
      setMessages((prev) => [
        ...prev.filter((m) => m.message_id !== optimistic.message_id),
        turn.user_message,
        turn.assistant_message,
      ]);
    } catch (err) {
      toastApiError("The assistant could not answer", err);
      setDraft(text);
      setMessages((prev) => prev.filter((m) => m.message_id !== optimistic.message_id));
    } finally {
      setSending(false);
    }
  };

  const remove = async (id: string) => {
    try {
      await deleteChat(await getToken(), id);
      if (id === chatId) navigate(BASE_PATH);
      void loadChats();
    } catch (err) {
      toastApiError("Could not delete the chat", err);
    }
  };

  return (
    <Group align="stretch" gap="md" wrap="nowrap" style={{ minHeight: 480 }}>
      <Stack gap="xs" w={240} style={{ flexShrink: 0 }}>
        <Button leftSection={<IconPlus size={14} />} onClick={() => setNewOpen(true)}>
          New chat
        </Button>
        {chats === null ? (
          <Loader size="xs" />
        ) : chats.length === 0 ? (
          <Text size="xs" c="dimmed">
            No chats yet.
          </Text>
        ) : (
          <ScrollArea.Autosize mah={560}>
            {chats.map((c) => (
              <NavLink
                key={c.chat_id}
                label={c.name}
                description={chatModelLabel(c)}
                active={c.chat_id === chatId}
                onClick={() => navigate(`${BASE_PATH}/${c.chat_id}`)}
                rightSection={
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    color="gray"
                    aria-label={`Delete ${c.name}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      void remove(c.chat_id);
                    }}
                  >
                    <IconTrash size={14} />
                  </ActionIcon>
                }
              />
            ))}
          </ScrollArea.Autosize>
        )}
      </Stack>
      <Paper withBorder radius="md" p="md" style={{ flex: 1, minWidth: 0 }}>
        {!chatId ? (
          <Stack gap="xs" align="flex-start">
            <Text fw={600}>Ask the Veeva user assistant</Text>
            <Text size="sm" c="dimmed">
              Create a chat, then ask in plain English. For example:
            </Text>
            {EXAMPLES.map((e) => (
              <Text key={e} size="sm">
                • {e}
              </Text>
            ))}
          </Stack>
        ) : (
          <Stack gap="md" h="100%">
            {chat && (
              <Text size="xs" c="dimmed">
                {chat.name} · {chatModelLabel(chat)}
              </Text>
            )}
            <ScrollArea.Autosize mah={560} type="auto">
              <Stack gap="lg" pr="sm">
                {messages.length === 0 && (
                  <Text size="sm" c="dimmed">
                    Try: “{EXAMPLES[0]}”
                  </Text>
                )}
                {messages.map((m) => (
                  <Turn key={m.message_id} message={m} proposals={proposals} />
                ))}
                {sending && (
                  <Group gap="xs">
                    <Loader size="xs" />
                    <Text size="sm" c="dimmed">
                      Working with Veeva…
                    </Text>
                  </Group>
                )}
                <div ref={bottom} />
              </Stack>
            </ScrollArea.Autosize>
            {!connectionId && (
              <Alert color="yellow" variant="light">
                Pick a Veeva connection above first.
              </Alert>
            )}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void send(draft);
              }}
            >
              <Group align="flex-end" gap="xs" wrap="nowrap">
                <Textarea
                  placeholder="Ask about Veeva users…"
                  value={draft}
                  onChange={(e) => setDraft(e.currentTarget.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      void send(draft);
                    }
                  }}
                  autosize
                  minRows={1}
                  maxRows={6}
                  style={{ flex: 1 }}
                  disabled={sending || !connectionId || !chat?.is_owner}
                />
                <Button
                  type="submit"
                  leftSection={<IconSend size={14} />}
                  loading={sending}
                  disabled={!draft.trim() || !connectionId}
                >
                  Send
                </Button>
              </Group>
            </form>
          </Stack>
        )}
      </Paper>
      <NewChatModal
        opened={newOpen}
        onClose={() => setNewOpen(false)}
        onCreated={(c) => {
          setNewOpen(false);
          void loadChats();
          navigate(`${BASE_PATH}/${c.chat_id}`);
        }}
      />
    </Group>
  );
}
